import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import {BehaviorSubject, Observable} from 'rxjs';
import {MsalService} from '@azure/msal-angular';
import {environment} from "../../environments/environment";
import {ChatMessage, ChatMessageListResponse} from '../models/chats/chatMessageListResponse';
import {BaseModel} from '../models/BaseModel';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private hubConnection!: signalR.HubConnection;

  // private grantApplicationId = '88f123e3-17d7-ef11-af6b-ac198ea83715'; //

  private grantApplicationId : string | undefined;
  private apiChatUrl = environment.apiConfig.uri + '/api/chatHub';
  private apiUrl = environment.apiConfig.uri + '/api/chat';
  private scopes = environment.apiConfig.scopes;


  private messagesSubject: BehaviorSubject<ChatMessage[]> = new BehaviorSubject<ChatMessage[]>([]);
  private moreLoadSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();
  public moreLoad$: Observable<boolean> = this.moreLoadSubject.asObservable();


  constructor(
    private msalService: MsalService,
    private http: HttpClient,
  ){


  }
  setGrantApplicationId(id: string) {
    this.grantApplicationId = id;
  }

  async getAccessToken(): Promise<string> {
    const request = {
      scopes: this.scopes
    };

    try {
      const result = await this.msalService.instance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (error) {

      console.error('Error acquiring access token', error);
      return '';
    }

  }

  public async startConnection(recievedCallback: any): Promise<void> {
    const token = await this.getAccessToken();


    if(token == '')
    { throw new Error('Authentication required'); }
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.apiChatUrl, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets, // Use `signalR.HttpTransportType` for clarity
        accessTokenFactory: async () => token
      })
      .build();

    this.hubConnection
      .start()
      .then(() => {
        this.hubConnection
          .invoke("JoinGrantApplicationGroup", this.grantApplicationId) //JoinGrantApplicationGroup is C# method name
          .catch((err) => {
            console.error(err);
          });
        this.registerOnServerEvents(recievedCallback);
      })
      .catch((err) => console.error('Error starting SignalR connection:', err));
  }

  /**
   * Drops the hub connection. The connection holds the access token it was built with, so signing
   * out does not close it -- it has to be stopped explicitly. Safe to call when nothing is
   * connected.
   */
  public stopConnection(): void {
    if (!this.hubConnection) {
      return;
    }

    this.hubConnection
      .stop()
      .catch((err) => console.error('Error stopping SignalR connection:', err));
  }




  /**
   * Keeps the server's ISO timestamp intact. Formatting belongs to the view layer only --
   * re-parsing a locale-formatted date string is what produced "Invalid Date" in the bubbles.
   */
  private static normalizeTimestamp(senton: string | null | undefined): string {
    if (senton && !isNaN(new Date(senton).getTime())) {
      return senton;
    }

    console.warn('ChatService: message arrived without a usable timestamp', senton);
    return new Date().toISOString(); // realtime push - receipt time is a safe approximation
  }

  private registerOnServerEvents(recievedCallback : any): void {
    this.hubConnection.on('ReceiveMessage', (user: string, message: string, senton: string, id?: string ) => {
      const newMsg: ChatMessage = {
        user: user,
        text: message,
        time: ChatService.normalizeTimestamp(senton),
        id: id
      };

      const currentMessages = this.messagesSubject.value;
      this.messagesSubject.next([...currentMessages, newMsg]);

      setTimeout(() => {
        recievedCallback();
      }, 500);

    });


    this.hubConnection.on('LoadRecentMessages', (recentMessages: ChatMessage[]) => {
      const currentMessages = this.messagesSubject.value;
      this.messagesSubject.next([...recentMessages, ...currentMessages]);
    });
  }


  public sendMessage(message: string): void {
    this.hubConnection.invoke('SendMessage', this.grantApplicationId, message, new Date().toISOString()).catch((err) => console.error(err));
  }

  public fetchMessages(latest: string): Promise<string> {
    const currentMessages = this.messagesSubject.value;
    return new Promise((resolve, reject) => { this.http.get<BaseModel<ChatMessageListResponse>>(`${this.apiUrl}/retrieve/${this.grantApplicationId}/${latest}`).subscribe(
      data => {
        const messages = data.item?.messages || [];

        if(data.item?.latest != null)
          latest = data.item?.latest

        if(messages.length){
          this.messagesSubject.next([...messages, ...currentMessages]);
        }else{
          this.moreLoadSubject.next(false);
        }
        resolve(latest);
      },
      error => {
        console.error('Error fetching chats:', error);
        reject(error);
      });
    });
  }

  public deleteMessage(messageId: string): Promise<boolean> {
    if (!this.grantApplicationId || !messageId) {
      return Promise.reject('Missing required parameters');
    }

    const deletePayload = {
      id: messageId,
      grantApplicationId: this.grantApplicationId
    };

    return new Promise((resolve, reject) => {
      this.http.delete<BaseModel<boolean>>(`${this.apiUrl}/DeleteMessage`, { body: deletePayload })
        .subscribe(
          response => {
            if (response.result) {
              // Remove the deleted message from the messages subject
              const currentMessages = this.messagesSubject.value;
              const updatedMessages = currentMessages.filter(msg => msg.id !== messageId);
              this.messagesSubject.next(updatedMessages);
              resolve(true);
            } else {
              reject(response.messages);
            }
          },
          error => {
            console.error('Error deleting message:', error);
            reject(error);
          }
        );
    });
  }
}
